import React, { MouseEvent, ReactElement, useEffect, useState } from "react";
import { SuperSimpleCarousel } from "@wss/carousel";
import { Image } from "@wss/image";
import { cls } from "@wss/cls";
import { useHomePageData } from "../utilities";
import { trackClickThroughStats } from "~/userAnalytics";
import { createPromotionView, handlePromotionClick, PromotionEvent, sendPromotionEvent, usePromotionTracking } from "~/Gtm/usePromotionTracking";
import { BannerItem, BannerItemImage } from "../HomePageResult";
const sortOrder = ["sm", "md", "lg"];
function sortImages(a: BannerItemImage, b: BannerItemImage): number {
  return sortOrder.indexOf(a.sizeSuffix) > sortOrder.indexOf(b.sizeSuffix) ? 1 : -1;
}
function handleBannerItemClick(e: MouseEvent<HTMLAnchorElement>): void {
  trackClickThroughStats(e);
  handlePromotionClick(e);
}
type HomePageAdBannerCarouselItemProps = {
  bannerItem: BannerItem;
  arr: BannerItem[];
  index: number;
};
function HomePageAdBannerCarouselItem({
  bannerItem,
  index,
  arr
}: HomePageAdBannerCarouselItemProps): ReactElement {
  return <div role="tabpanel" id={`tabpanel${index}`} aria-label={`Slide ${index + 1} of ${arr.length}`} data-sentry-component="HomePageAdBannerCarouselItem" data-sentry-source-file="index.tsx">
      <a href={bannerItem.url} aria-label={bannerItem.altText} className="text-white w-full gtm-promo" data-ctt="1" data-ctd={bannerItem.bannerId} data-id={bannerItem.bannerId} data-name={bannerItem.altText} data-position={index + 1} data-isbanner onClick={handleBannerItemClick}>
        <picture className="flex" data-sentry-element="picture" data-sentry-source-file="index.tsx">
          {bannerItem.images.sort(sortImages).map(image => image.sizeSuffix === "sm" ? <source key={image.imagePath} media="(max-width: 599px)" srcSet={image.imagePath} width={5} height={4} /> : image.sizeSuffix === "md" ? <source key={image.imagePath} media="(min-width: 600px) and (max-width: 1499px)" srcSet={image.imagePath} width={180} height={49} /> : <Image key={image.imagePath} className="b-loaded w-full h-auto" fetchpriority={index === 0 ? "high" : "low"} alt={bannerItem.altText} src={image.imagePath} width={1721} height={469} />)}
        </picture>
      </a>
    </div>;
}
type HomePageAdBannerPaginationProps = {
  active: number;
  index: number;
};
function HomePageAdBannerPagination({
  active,
  index
}: HomePageAdBannerPaginationProps): ReactElement {
  const carousel = SuperSimpleCarousel.useContext();
  function handleOnClick() {
    carousel.goTo(index);
  }
  return <button onClick={handleOnClick} type="button" role="tab" onFocus={carousel.stopAutoplay} onBlur={carousel.startAutoplay} aria-label={`Slide ${index + 1}`} aria-selected={index === active ? "true" : "false"} aria-controls={`tabpanel${index}`} className={cls("rounded-full border-0 inline-block h-4 mx-1 overflow-hidden p-0 text-transparent w-4", index === active ? "shadow-[0_0_7px_0_#383838] bg-white/70" : "shadow-[0_0_7px_0_#ffffff] bg-black/60")} data-sentry-component="HomePageAdBannerPagination" data-sentry-source-file="index.tsx">
      {index + 1}
    </button>;
}
const HomePageAdBanner = () => {
  const {
    banner
  } = useHomePageData();
  const [active, setActive] = useState<number>(0);
  const carousel = SuperSimpleCarousel.useCarousel({
    autoplay: true
  });
  useEffect(function handleTrackActiveItem() {
    if (!carousel.carouselRef.current?.children) return;
    function handleIntersection(entries: IntersectionObserverEntry[]) {
      const children = carousel.carouselRef.current?.children;
      if (!children || !entries[0]) return;
      setActive(Array.from(children).indexOf(entries[0].target));
    }
    const observer = new IntersectionObserver(handleIntersection, {
      root: carousel.carouselRef.current,
      threshold: [0.9]
    });
    for (const child of carousel.carouselRef.current.children) {
      observer.observe(child);
    }
    return function cleanupTrackActiveItem() {
      return observer.disconnect();
    };
  }, [carousel.carouselRef]);
  useEffect(function initDelayedPromotionTracking() {
    if (!carousel.carouselRef.current?.children) return;
    let timeout: NodeJS.Timeout;
    function handleIntersection(entries: IntersectionObserverEntry[]) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          if (timeout) clearTimeout(timeout);
          timeout = setTimeout(function onSettled() {
            observer.unobserve(entry.target);
            const promotionDetails: PromotionEvent = createPromotionView(entry.target);
            sendPromotionEvent("promotionView", promotionDetails);
          }, 250);
        }
      }
    }
    const observer = new IntersectionObserver(handleIntersection, {
      root: carousel.carouselRef.current,
      threshold: [0.8]
    });
    const carouselElem = carousel.carouselRef.current;
    const gtmPromotions = carouselElem.querySelectorAll(".gtm-promo");
    for (const child of gtmPromotions) {
      observer.observe(child);
    }
    return function cleanupTrackActiveItem() {
      return observer.disconnect();
    };
  }, [carousel.carouselRef]);
  return <section className="lt:px-[15px] lz:px-10 xxl:px-[7%] mx-auto lt:max-w-[916px] lg:max-w-[974px] lz:max-w-[1200px] xxl:max-w-[1920px]" data-sentry-component="HomePageAdBanner" data-sentry-source-file="index.tsx">
      <SuperSimpleCarousel carousel={carousel} data-sentry-element="SuperSimpleCarousel" data-sentry-source-file="index.tsx">
        <SuperSimpleCarousel.Content className="m-0" threshold={0.5} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
          {banner.map((bannerItem, index, self) => <SuperSimpleCarousel.Item key={bannerItem.bannerId} className="w-full flex-shrink-0">
              <HomePageAdBannerCarouselItem bannerItem={bannerItem} index={index} arr={self} />
            </SuperSimpleCarousel.Item>)}
        </SuperSimpleCarousel.Content>
        <div className="absolute left-1/2 -translate-x-1/2 bottom-3" role="tablist">
          {banner.map((item, index) => <HomePageAdBannerPagination key={item.bannerId} index={index} active={active} />)}
        </div>
      </SuperSimpleCarousel>
    </section>;
};
export default HomePageAdBanner;